/**
 * Renders a single assessment form (AssessmentSpecDTO items) and submits via practitioner/assessments/run.
 * Submit API: POST /api/secure/practitioner/assessments/run (submitAssessmentRun) with
 *   bookedEventId (mandatory), parentBookedEventId (when from drawer), clinicalAssessmentId, assessmentId, answers.
 * Results: GET /api/secure/practitioner/assessments/runs (getAssessmentRuns).
 * Uses superText/supertext above the question and subText/subtext below (like consumer AssessmentPlayer).
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Checkbox,
  TextField,
  Typography,
  Paper,
  Tooltip,
  alpha,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  getAssessmentRunResult,
  getRunContext,
  submitAssessmentRun,
  saveAssessmentRunProgressDraft,
} from 'src/mentor/services/practitionerAssessmentService'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

const ANSWER_TYPES = {
  optionSelectSingle: 'optionSelectSingle',
  optionSelectMultiple: 'optionSelectMultiple',
  textBox: 'textBox',
}

/** Default domain names when none provided; common assessment domains for "Calculating/Completed X score" messages. */
const DEFAULT_DOMAIN_NAMES = ['Overall', 'Depression', 'Anxiety', 'Wellbeing', 'Functioning']

/** Normalize assessmentDomains to string[] (items may be strings or { name, domainName, title }). */
function getDomainNamesFromAssessmentDomains(assessmentDomains) {
  if (!Array.isArray(assessmentDomains) || assessmentDomains.length === 0) return null
  const names = assessmentDomains
    .map((d) => (typeof d === 'string' ? d : d?.name ?? d?.domainName ?? d?.title))
    .filter(Boolean)
  return names.length > 0 ? names : null
}

/** Build rotating messages: "Calculating <Domain> score…" and "Completed <Domain> score" then final lines. */
function buildSubmitLoaderMessages(domainNames) {
  const names = Array.isArray(domainNames) && domainNames.length > 0 ? domainNames : DEFAULT_DOMAIN_NAMES
  const domainMessages = names.flatMap((name) => [
    `Calculating ${name} score…`,
    `Completed ${name} score`,
  ])
  return [
    ...domainMessages,
    'Preparing your report…',
    'Almost there…',
    'Finalising your report…',
  ]
}

const SUBMIT_LOADER_MESSAGE_INTERVAL_MS = 2800
const DRAFT_AUTOSAVE_DEBOUNCE_MS = 600
const DRAFT_AUTOSAVE_RETRY_MS = 1200
const MIN_OPTIONS_FOR_TWO_COLUMN_LAYOUT = 6

function isEmptyDraftAnswerValue(value) {
  if (value == null) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'string') return value.trim() === ''
  return false
}

function areDraftAnswerValuesEqual(left, right) {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false
    if (left.length !== right.length) return false
    for (let i = 0; i < left.length; i += 1) {
      if (left[i] !== right[i]) return false
    }
    return true
  }
  return left === right
}

// API may return kebab-case (e.g. option-select-single); normalize to camelCase for checks
function normalizeAnswerType(answerType) {
  if (!answerType) return answerType
  const t = String(answerType)
  if (t === 'option-select-single') return ANSWER_TYPES.optionSelectSingle
  if (t === 'option-select-multiple') return ANSWER_TYPES.optionSelectMultiple
  if (t === 'text-box') return ANSWER_TYPES.textBox
  return t
}

// Gradient colors for ordered scales (low/worst → high/best). Theme palette keys or fallback hex.
const GRADIENT_PALETTE = ['error.main', 'warning.dark', 'warning.main', 'success.light', 'success.main']

/** Whether options look like a logical gradient scale (e.g. Never→Very Often, Poor→Excellent, Low→High) */
function isLogicalGradient(options) {
  if (!Array.isArray(options) || options.length < 2 || options.length > 7) return false
  const labels = options.map((o) => String((o.label ?? o.title ?? '')).toLowerCase().trim())
  const gradientPatterns = [
    /never|rarely|sometimes|often|very often|always/,
    /not at all|just a little|quite a bit|very much/,
    /poor|fair|good|very good|excellent|outstanding/,
    /low|medium|moderate|high|very high/,
    /strongly disagree|disagree|neutral|agree|strongly agree/,
    /none|mild|moderate|severe|very severe/,
  ]
  const matchCount = labels.filter((l) => gradientPatterns.some((p) => p.test(l))).length
  if (matchCount >= Math.min(2, labels.length)) return true
  // Likert-style length (3–7 options) with ordered weights or sequential order
  const hasWeights = options.some((o) => o.weight != null)
  if (hasWeights && options.length >= 3) return true
  return options.length >= 3 && options.length <= 5
}

/** Get gradient color for option at index (0 = low, total-1 = high). Returns resolved theme color. */
function getOptionGradientColor(theme, index, total) {
  if (!theme?.palette || total <= 1) return null
  const step = total === 1 ? 0 : index / (total - 1)
  const paletteIndex = Math.round(step * (GRADIENT_PALETTE.length - 1))
  const key = GRADIENT_PALETTE[paletteIndex]
  const [area, shade] = String(key).split('.')
  return theme.palette[area]?.[shade] ?? theme.palette?.error?.main
}

function getOptionsGridTemplate(optionCount) {
  if (Number(optionCount) < MIN_OPTIONS_FOR_TWO_COLUMN_LAYOUT) return '1fr'
  // Keep one column on narrower widths to prevent option text collisions.
  return { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
}

const OPTION_LABEL_SX = {
  display: 'block',
  width: '100%',
  minWidth: 0,
  lineHeight: 1.4,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}

/**
 * Build items array from journalQusAns when API returns QnA only via journalQusAns (e.g. after qnaUnlocked).
 * Per practitioner-assessment.md: journalQusAns has id, question, answerType, options, subText, superText, helpText.
 */
function buildItemsFromJournalQusAns(journalQusAns) {
  if (!Array.isArray(journalQusAns) || journalQusAns.length === 0) return []
  return journalQusAns.map((j) => {
    const itemId = j.id ?? j.qusId ?? j.questionId
    const rawOptions = j.options ?? []
    const options = rawOptions.map((o) => ({
      id: o.id,
      optionId: o.optionId ?? o.id,
      label: o.label ?? o.title ?? o.name,
      title: o.title ?? o.label ?? o.name,
      name: o.name ?? o.label ?? o.title,
    }))
    return {
      itemId,
      label: j.question ?? j.label,
      question: j.question,
      options,
      answerType: j.answerType,
      superText: j.superText ?? j.supertext,
      subText: j.subText ?? j.subtext,
      helpText: j.helpText ?? j.helptext,
    }
  })
}

export default function PractitionerAssessmentRunner({
  assessmentSpec,
  clinicalAssessment,
  clinicalAssessmentId,
  psychAssessmentId,
  assessmentId,
  /** Assessment event id (mandatory for run API). From assessment/book response (drawer) or session event (assessment report page). */
  bookedEventId,
  /** Parent session event (therapy/consultation/screening). Pass only when run is from Assessment drawer. */
  parentBookedEventId,
  /** When true, bookedEventId is the assessment event (assessment report page). When false, parent passes assessment event via bookedEventId and parent via parentBookedEventId. */
  isAssessmentEventPage = false,
  /** Prefill from run-context latestRunAnswers when opening runner. */
  initialRunAnswers,
  /** From run-context: latestDraftRunId for PATCH /run/progress-draft continuation. */
  initialDraftRunId = null,
  /** From run-context: latestDraftRunRevision for optimistic draft patching. */
  initialDraftRevision = null,
  /** From run-context: latest completed run id for this assessment (if any). */
  latestCompletedRunId = null,
  onSuccess,
  onBack,
  onViewResult,
  draftKey,
  /** Optional domain names for submit loader: "Calculating/Completed <Name> score". Uses defaults if omitted. */
  domainNames,
}) {
  const clinicalId = clinicalAssessmentId ?? psychAssessmentId
  const assessmentEventId = bookedEventId
  const parentId = parentBookedEventId
  const journalQusAns = assessmentSpec?.journalQusAns ?? clinicalAssessment?.journalQusAns ?? []
  const itemsFromSpec = assessmentSpec?.items ?? []
  const rawItems =
    itemsFromSpec.length > 0
      ? itemsFromSpec
      : buildItemsFromJournalQusAns(journalQusAns)
  // Enrich items with superText/subText/helpText from journalQusAns so they show on resume and when spec.items lack them
  const journalByItemId = useMemo(() => {
    const m = new Map()
    for (const j of journalQusAns || []) {
      const id = j.id ?? j.qusId ?? j.questionId
      if (id) m.set(id, j)
    }
    return m
  }, [journalQusAns])
  const items = useMemo(() => {
    return (rawItems || []).map((item) => {
      const j = journalByItemId.get(item.itemId)
      if (!j) return item
      return {
        ...item,
        superText: item?.superText ?? item?.supertext ?? j.superText ?? j.supertext,
        subText: item?.subText ?? item?.subtext ?? j.subText ?? j.subtext,
        helpText: item?.helpText ?? item?.helptext ?? j.helpText ?? j.helptext,
      }
    })
  }, [rawItems, journalByItemId])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState(() => {
    const initial = {}
    items.forEach((item) => {
      if (normalizeAnswerType(item.answerType) === ANSWER_TYPES.optionSelectMultiple) {
        initial[item.itemId] = []
      } else {
        initial[item.itemId] = ''
      }
    })
    if (initialRunAnswers && typeof initialRunAnswers === 'object') {
      Object.keys(initialRunAnswers).forEach((key) => {
        if (initial[key] !== undefined) initial[key] = initialRunAnswers[key]
      })
    }
    return initial
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitLoaderMessageIndex, setSubmitLoaderMessageIndex] = useState(0)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null)
  const lastRunRef = useRef(null)
  const [draftRunState, setDraftRunState] = useState(() => ({
    draftRunId: initialDraftRunId ?? null,
    draftRevision:
      typeof initialDraftRevision === 'number' && Number.isFinite(initialDraftRevision)
        ? initialDraftRevision
        : null,
  }))
  const draftRunStateRef = useRef(draftRunState)
  const pendingDraftPatchRef = useRef({ answersPatch: {}, removeAnswerItemIds: new Set() })
  const draftPatchInFlightRef = useRef(false)
  const draftFlushTimerRef = useRef(null)
  const previousAnswersRef = useRef(null)
  const draftDiffInitializedRef = useRef(false)

  const initialAnswers = useMemo(() => {
    const o = {}
    items.forEach((item) => {
      if (normalizeAnswerType(item.answerType) === ANSWER_TYPES.optionSelectMultiple) {
        o[item.itemId] = []
      } else {
        o[item.itemId] = ''
      }
    })
    return o
  }, [items])

  useEffect(() => {
    draftRunStateRef.current = draftRunState
  }, [draftRunState])

  useEffect(() => {
    setDraftRunState({
      draftRunId: initialDraftRunId ?? null,
      draftRevision:
        typeof initialDraftRevision === 'number' && Number.isFinite(initialDraftRevision)
          ? initialDraftRevision
          : null,
    })
  }, [initialDraftRunId, initialDraftRevision, assessmentId, assessmentEventId])

  const submitLoaderMessages = useMemo(() => {
    const fromProp = Array.isArray(domainNames) && domainNames.length > 0 ? domainNames : null
    const fromSpec = getDomainNamesFromAssessmentDomains(assessmentSpec?.assessmentDomains)
    const fromClinical = getDomainNamesFromAssessmentDomains(clinicalAssessment?.assessmentDomains)
    const resolved =
      fromProp ??
      fromSpec ??
      fromClinical ??
      getDomainNamesFromAssessmentDomains(assessmentSpec?.domains) ??
      getDomainNamesFromAssessmentDomains(clinicalAssessment?.domains)
    return buildSubmitLoaderMessages(resolved)
  }, [domainNames, assessmentSpec?.assessmentDomains, assessmentSpec?.domains, clinicalAssessment?.assessmentDomains, clinicalAssessment?.domains])

  // Rotate interstitial message while submitting (builds confidence, no freeze)
  useEffect(() => {
    if (!submitting) {
      setSubmitLoaderMessageIndex(0)
      return
    }
    const id = setInterval(() => {
      setSubmitLoaderMessageIndex((i) => (i + 1) % submitLoaderMessages.length)
    }, SUBMIT_LOADER_MESSAGE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [submitting, submitLoaderMessages.length])
  const draftLoadedRef = useRef(false)
  /** True after we've run draft restore once; prevents showing "Back to list" before restore (avoids accidental exit). */
  const [hasAttemptedDraftRestore, setHasAttemptedDraftRestore] = useState(() => !draftKey)
  useEffect(() => {
    if (!draftKey || !items.length) {
      setHasAttemptedDraftRestore(true)
      return
    }
    if (draftLoadedRef.current) return
    try {
      const raw = sessionStorage.getItem(draftKey)
      if (raw) {
        const draft = JSON.parse(raw)
        if (draft?.answers && typeof draft.answers === 'object') {
          setAnswers((prev) => ({ ...prev, ...draft.answers }))
        }
        const localDraftRunId = draft?.draftRunId ?? draft?.latestDraftRunId ?? null
        const localDraftRevision =
          typeof draft?.draftRevision === 'number' && Number.isFinite(draft?.draftRevision)
            ? draft.draftRevision
            : null
        if (localDraftRunId || localDraftRevision != null) {
          setDraftRunState((prev) => ({
            draftRunId: localDraftRunId ?? prev?.draftRunId ?? null,
            draftRevision: localDraftRevision ?? prev?.draftRevision ?? null,
          }))
        }
        if (typeof draft?.currentIndex === 'number' && draft.currentIndex >= 0 && draft.currentIndex < items.length) {
          setCurrentIndex(draft.currentIndex)
        }
        draftLoadedRef.current = true
      }
    } catch (e) {}
    setHasAttemptedDraftRestore(true)
  }, [draftKey, items.length])
  useEffect(() => {
    if (!draftKey) return
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(
          draftKey,
          JSON.stringify({
            assessmentId,
            clinicalAssessmentId: clinicalId,
            psychAssessmentId: clinicalId,
            assessmentEventBookedEventId: assessmentEventId ?? null,
            parentBookedEventId: parentId ?? null,
            draftRunId: draftRunState?.draftRunId ?? null,
            draftRevision: draftRunState?.draftRevision ?? null,
            answers,
            currentIndex,
          }),
        )
      } catch (e) {}
    }, 400)
    return () => clearTimeout(t)
  }, [
    draftKey,
    assessmentId,
    clinicalId,
    assessmentEventId,
    parentId,
    draftRunState,
    answers,
    currentIndex,
  ])

  const total = items.length
  const safeIndex = total > 0 ? Math.min(currentIndex, total - 1) : 0
  const currentItem = items[safeIndex]
  const progress = total ? ((safeIndex + 1) / total) * 100 : 0

  // Clamp currentIndex if total shrunk (e.g. props changed) so we never render out-of-bounds
  useEffect(() => {
    if (total > 0 && currentIndex >= total) {
      setCurrentIndex(total - 1)
    }
  }, [total, currentIndex])
  const journalItem =
    journalQusAns[safeIndex] ??
    (currentItem?.itemId && journalQusAns.find((j) => (j.qusId ?? j.questionId) === currentItem.itemId))

  // When enforceWhitelist is true, only show/accept options in whitelistOptionIds (per API spec)
  const getOptionsForItem = useCallback(
    (item) => {
      const raw = item?.options ?? []
      if (
        assessmentSpec?.enforceWhitelist &&
        Array.isArray(assessmentSpec.whitelistOptionIds) &&
        assessmentSpec.whitelistOptionIds.length > 0
      ) {
        return raw.filter((opt) => assessmentSpec.whitelistOptionIds.includes(opt.optionId ?? opt.id))
      }
      return raw
    },
    [assessmentSpec?.enforceWhitelist, assessmentSpec?.whitelistOptionIds],
  )

  const flushDraftProgressPatchRef = useRef(null)

  const scheduleDraftPatchFlush = useCallback(
    (delayMs = DRAFT_AUTOSAVE_DEBOUNCE_MS) => {
      if (!assessmentEventId || !clinicalId || !assessmentId || submitSuccess) return
      if (draftFlushTimerRef.current) {
        clearTimeout(draftFlushTimerRef.current)
      }
      draftFlushTimerRef.current = setTimeout(() => {
        if (typeof flushDraftProgressPatchRef.current === 'function') {
          void flushDraftProgressPatchRef.current()
        }
      }, delayMs)
    },
    [assessmentEventId, clinicalId, assessmentId, submitSuccess],
  )

  const queueDraftPatchForAnswer = useCallback(
    (itemId, value) => {
      if (!itemId || !assessmentEventId || !clinicalId || !assessmentId || submitSuccess) return
      const pending = pendingDraftPatchRef.current
      if (isEmptyDraftAnswerValue(value)) {
        delete pending.answersPatch[itemId]
        pending.removeAnswerItemIds.add(itemId)
      } else {
        pending.answersPatch[itemId] = value
        pending.removeAnswerItemIds.delete(itemId)
      }
      scheduleDraftPatchFlush()
    },
    [assessmentEventId, clinicalId, assessmentId, submitSuccess, scheduleDraftPatchFlush],
  )

  const flushDraftProgressPatch = useCallback(
    async ({ force = false } = {}) => {
      if (!assessmentEventId || !clinicalId || !assessmentId || submitSuccess) return true
      if (!force && submitting) return true
      if (draftPatchInFlightRef.current) {
        if (!force) scheduleDraftPatchFlush(DRAFT_AUTOSAVE_RETRY_MS)
        return false
      }
      const pending = pendingDraftPatchRef.current
      const answersPatch = { ...pending.answersPatch }
      const removeAnswerItemIds = Array.from(pending.removeAnswerItemIds)
      if (Object.keys(answersPatch).length === 0 && removeAnswerItemIds.length === 0) return true
      pendingDraftPatchRef.current = { answersPatch: {}, removeAnswerItemIds: new Set() }
      draftPatchInFlightRef.current = true
      try {
        const latestDraftState = draftRunStateRef.current
        const payload = {}
        const hasKnownDraft =
          Boolean(latestDraftState?.draftRunId) &&
          typeof latestDraftState?.draftRevision === 'number' &&
          Number.isFinite(latestDraftState?.draftRevision)
        if (hasKnownDraft) {
          payload.draftRunId = latestDraftState.draftRunId
          payload.draftRevision = latestDraftState.draftRevision
        } else {
          payload.bookedEventId = assessmentEventId
          if (parentId) payload.parentBookedEventId = parentId
          payload.clinicalAssessmentId = clinicalId
          payload.assessmentId = assessmentId
        }
        if (Object.keys(answersPatch).length > 0) payload.answersPatch = answersPatch
        if (removeAnswerItemIds.length > 0) payload.removeAnswerItemIds = removeAnswerItemIds

        const result = await saveAssessmentRunProgressDraft(payload)
        if (result?.success) {
          setDraftRunState((prev) => ({
            draftRunId: result?.draftRunId ?? prev?.draftRunId ?? null,
            draftRevision:
              typeof result?.draftRevision === 'number' && Number.isFinite(result?.draftRevision)
                ? result.draftRevision
                : (prev?.draftRevision ?? null),
          }))
          return true
        }

        if (result?.assessmentCompleted || result?.code === 4038) {
          return false
        }

        if (result?.conflict) {
          const serverDraftAnswers =
            result?.latestDraftAnswers && typeof result.latestDraftAnswers === 'object'
              ? result.latestDraftAnswers
              : null
          if (serverDraftAnswers) {
            setAnswers({ ...initialAnswers, ...serverDraftAnswers })
          }
          setDraftRunState((prev) => ({
            draftRunId: result?.draftRunId ?? prev?.draftRunId ?? null,
            draftRevision:
              typeof result?.draftRevision === 'number' && Number.isFinite(result?.draftRevision)
                ? result.draftRevision
                : (prev?.draftRevision ?? null),
          }))
        }

        // Requeue unsent patch (network/conflict) and retry with latest revision state.
        const retryPending = pendingDraftPatchRef.current
        Object.entries(answersPatch).forEach(([itemId, value]) => {
          retryPending.answersPatch[itemId] = value
          retryPending.removeAnswerItemIds.delete(itemId)
        })
        removeAnswerItemIds.forEach((itemId) => {
          delete retryPending.answersPatch[itemId]
          retryPending.removeAnswerItemIds.add(itemId)
        })
        scheduleDraftPatchFlush(result?.conflict ? 0 : DRAFT_AUTOSAVE_RETRY_MS)
        return false
      } finally {
        draftPatchInFlightRef.current = false
      }
    },
    [
      assessmentEventId,
      clinicalId,
      assessmentId,
      parentId,
      initialAnswers,
      submitting,
      submitSuccess,
      scheduleDraftPatchFlush,
    ],
  )

  useEffect(() => {
    flushDraftProgressPatchRef.current = flushDraftProgressPatch
  }, [flushDraftProgressPatch])

  useEffect(() => {
    if (!hasAttemptedDraftRestore) return
    if (submitting || submitSuccess) return
    if (!draftDiffInitializedRef.current) {
      previousAnswersRef.current = answers
      draftDiffInitializedRef.current = true
      return
    }
    const previous = previousAnswersRef.current ?? {}
    const current = answers ?? {}
    const itemIds = new Set([...Object.keys(previous), ...Object.keys(current)])
    itemIds.forEach((itemId) => {
      if (!areDraftAnswerValuesEqual(previous[itemId], current[itemId])) {
        queueDraftPatchForAnswer(itemId, current[itemId])
      }
    })
    previousAnswersRef.current = current
  }, [answers, hasAttemptedDraftRestore, queueDraftPatchForAnswer, submitting, submitSuccess])

  useEffect(() => {
    return () => {
      if (draftFlushTimerRef.current) {
        clearTimeout(draftFlushTimerRef.current)
      }
    }
  }, [])

  const goNext = useCallback(() => {
    if (currentIndex < total - 1) {
      setCurrentIndex((i) => i + 1)
    }
  }, [currentIndex, total])

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1)
    }
  }, [currentIndex])

  const setAnswer = useCallback((itemId, value) => {
    setAnswers((prev) => ({ ...prev, [itemId]: value }))
  }, [])

  const toggleMultiOption = useCallback((itemId, optionId) => {
    setAnswers((prev) => {
      const current = prev[itemId]
      const arr = Array.isArray(current) ? [...current] : []
      const idx = arr.indexOf(optionId)
      if (idx === -1) {
        arr.push(optionId)
      } else {
        arr.splice(idx, 1)
      }
      return { ...prev, [itemId]: arr }
    })
  }, [])

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!assessmentEventId) {
      setSubmitError('Assessment booking id is missing. Please refresh and try again.')
      return
    }
    if (draftFlushTimerRef.current) {
      clearTimeout(draftFlushTimerRef.current)
      draftFlushTimerRef.current = null
    }
    // Best-effort final draft flush before submit.
    await flushDraftProgressPatch({ force: true })
    setSubmitting(true)
    try {
      const openCompletedResult = async (completedRunId, fallbackRun = null, fallbackReport = null) => {
        if (!onViewResult || !completedRunId) return false
        const runResult = await getAssessmentRunResult({ completedRunId })
        if (!runResult?.error) {
          onViewResult(assessmentId, assessmentSpec?.title, {
            data: {
              assessmentRun: runResult?.assessmentRun ?? null,
              assessmentRunView: runResult?.assessmentRunView ?? null,
              assessmentReport: runResult?.assessmentReport ?? null,
            },
          })
          return true
        }
        onViewResult(assessmentId, assessmentSpec?.title, {
          data: {
            assessmentRun: fallbackRun,
            assessmentRunView: null,
            assessmentReport: fallbackReport,
          },
        })
        return true
      }

      // Cross-check: if this assessment is already completed, don't call POST /run again.
      if (latestCompletedRunId) {
        const movedToResult = await openCompletedResult(latestCompletedRunId)
        if (movedToResult) return
      }

      const payload = {
        bookedEventId: assessmentEventId,
        clinicalAssessmentId: clinicalId,
        assessmentId,
        answers,
      }
      if (parentId) payload.parentBookedEventId = parentId
      const res = await submitAssessmentRun(payload)
      const resCode = res?.data?.code ?? res?.response?.data?.code
      const resMessage = res?.data?.message ?? res?.response?.data?.message
      const resData = res?.data?.data ?? res?.response?.data?.data ?? res?.data ?? res?.response?.data
      const isSuccess = res?.status === 200 && isCommonResponseSuccessful(res?.data?.code)
      if (isSuccess) {
        const data = resData
        lastRunRef.current = data ?? null
        pendingDraftPatchRef.current = { answersPatch: {}, removeAnswerItemIds: new Set() }
        setDraftRunState({ draftRunId: null, draftRevision: null })
        if (draftKey) {
          try {
            sessionStorage.removeItem(draftKey)
          } catch (e) {}
        }
        setSubmitSuccess(true)
      } else {
        if (resCode === 40992) {
          const runContext = await getRunContext({
            bookedEventId: assessmentEventId,
            parentBookedEventId: parentId,
            clinicalAssessmentId: clinicalId,
            assessmentId,
          })
          const completedRunId = runContext?.latestCompletedRunId ?? null
          const isCompleted = runContext?.assessmentCompleted === true || Boolean(completedRunId)
          if (isCompleted && completedRunId) {
            const movedToResult = await openCompletedResult(
              completedRunId,
              runContext?.latestCompletedAssessmentRun ?? null,
              null,
            )
            if (movedToResult) return
          }
          setSubmitError('This assessment is already completed. Opening latest result was not possible; please try View Result.')
          return
        }
        const msg =
          resCode === 4037
            ? 'Payment pending for this assessment. Ask patient to complete payment, then try again.'
            : resMessage || 'Failed to submit assessment'
        setSubmitError(msg)
      }
    } catch (e) {
      setSubmitError('Failed to submit assessment')
    } finally {
      setSubmitting(false)
    }
  }

  const isLast = safeIndex === total - 1
  const currentAnswerType = currentItem ? normalizeAnswerType(currentItem.answerType) : null
  const canProceed =
    currentItem &&
    (currentAnswerType === ANSWER_TYPES.textBox
      ? answers[currentItem.itemId] != null && String(answers[currentItem.itemId]).trim() !== ''
      : currentAnswerType === ANSWER_TYPES.optionSelectSingle
        ? answers[currentItem.itemId] != null && answers[currentItem.itemId] !== ''
        : true)

  const currentNum = safeIndex + 1
  const prevNum = safeIndex
  const nextNum = safeIndex + 2

  const backButtonLabel = 'Back to list'
  const prevButtonLabel = total > 1 ? `Previous (Question ${prevNum} of ${total})` : 'Previous'
  const nextButtonLabel = total > 1 ? `Next (Question ${nextNum} of ${total})` : 'Next'
  const submitButtonLabel = submitting ? 'Submitting…' : 'Submit assessment'
  /** When resuming (draftKey), wait for draft restore before allowing "Back to list" to avoid accidental exit. */
  const canShowBackToList = safeIndex === 0 && (hasAttemptedDraftRestore || !draftKey)

  const handleBackAfterSubmit = () => {
    onSuccess?.(lastRunRef.current)
  }

  const handleBackToList = useCallback(() => {
    setActionsMenuAnchor(null)
    if (isLast && !window.confirm('Leave without submitting? Your progress is saved as draft.')) {
      return
    }
    onBack?.()
  }, [onBack, isLast])

  const handleRestartFromBeginning = useCallback(() => {
    setActionsMenuAnchor(null)
    if (draftKey) {
      try {
        sessionStorage.removeItem(draftKey)
      } catch (e) {}
    }
    setAnswers(initialAnswers)
    setCurrentIndex(0)
    setSubmitError(null)
  }, [draftKey, initialAnswers])

  const handleClearAnswers = useCallback(() => {
    setActionsMenuAnchor(null)
    if (draftKey) {
      try {
        sessionStorage.removeItem(draftKey)
      } catch (e) {}
    }
    setAnswers(initialAnswers)
    setCurrentIndex(0)
    setSubmitError(null)
  }, [draftKey, initialAnswers])

  if (!assessmentSpec || !items.length) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">No questions in this assessment.</Typography>
      </Box>
    )
  }

  if (submitSuccess) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', alignItems: 'center', p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Assessment submitted
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Results are saved. View scores and detailed reports below or return to the list.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 280 }}>
          {onViewResult && (
            <Button
              variant="contained"
              onClick={() => onViewResult(assessmentId, assessmentSpec?.title, lastRunRef.current)}
              fullWidth
            >
              View result
            </Button>
          )}
          <Button variant="outlined" onClick={handleBackAfterSubmit} startIcon={<ArrowBackIcon />} fullWidth>
            Back to list
          </Button>
        </Box>
      </Box>
    )
  }

  const superText =
    currentItem?.superText ?? currentItem?.supertext ?? journalItem?.superText ?? journalItem?.supertext ?? null
  const subTextContent =
    currentItem?.subText ?? currentItem?.subtext ?? journalItem?.subText ?? journalItem?.subtext ?? null
  const helpText = currentItem?.helpText ?? currentItem?.helptext ?? journalItem?.helpText ?? journalItem?.helptext ?? null
  const questionLabel = currentItem?.label ?? journalItem?.question ?? currentItem?.label

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundImage: (theme) =>
          `linear-gradient(160deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(theme.palette.info.light, 0.1)} 35%, ${alpha(theme.palette.background.default, 0.96)} 100%)`,
      }}
    >
      {/* Interstitial overlay while submitting: hides answers, shows loader + rotating confidence messages */}
      {submitting && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.97),
            borderRadius: 1,
          }}
        >
          <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
          <Box sx={{ textAlign: 'center', px: 3 }}>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {submitLoaderMessages[submitLoaderMessageIndex]}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This usually takes a few seconds. Please don&apos;t close this window.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Header: title + progress + actions menu */}
      <Paper
        elevation={0}
        square
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.88),
          backdropFilter: 'blur(6px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ flex: 1, minWidth: 0 }}>
            {assessmentSpec.title}
          </Typography>
          <Tooltip title="Options">
            <IconButton
              size="small"
              onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
              disabled={submitting}
              aria-label="Assessment options"
              sx={{ flexShrink: 0 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor)}
          onClose={() => setActionsMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleBackToList}>
            <ListItemIcon>
              <ArrowBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Back to list</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleRestartFromBeginning}>
            <ListItemIcon>
              <ReplayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Restart from beginning</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleClearAnswers}>
            <ListItemIcon>
              <ClearAllIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clear answers</ListItemText>
          </MenuItem>
        </Menu>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }}
          />
          <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ minWidth: 72 }}>
            {currentNum} of {total}
          </Typography>
        </Box>
      </Paper>

      {/* Question card + nav buttons (kept together so buttons sit close to options, not at bottom of page) */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: 1,
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
            boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography
              component="span"
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                fontWeight: 700,
                color: 'primary.main',
                fontSize: '0.75rem',
              }}
            >
              {(superText ? String(superText).toUpperCase() : 'QUESTION')}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              {currentNum} of {total}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant="h6" sx={{ mb: subTextContent ? 1.5 : 2, fontWeight: 600, lineHeight: 1.4, flex: 1 }}>
              {questionLabel}
            </Typography>
            {helpText && (
              <Tooltip title={helpText} arrow placement="top">
                <IconButton size="small" sx={{ p: 0.25, mt: -0.25 }} aria-label="Help">
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {subTextContent && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                lineHeight: 1.5,
                color: '#64748b',
                bgcolor: 'action.hover',
                px: 1.5,
                py: 1,
                borderRadius: 1,
                borderLeft: '4px solid',
                borderColor: 'info.light',
              }}
            >
              {subTextContent}
            </Typography>
          )}

          {currentAnswerType === ANSWER_TYPES.optionSelectSingle && (() => {
            const opts = getOptionsForItem(currentItem)
            const useGradient = isLogicalGradient(opts)
            return (
              <RadioGroup
                value={answers[currentItem.itemId] ?? ''}
                onChange={(e) => setAnswer(currentItem.itemId, e.target.value)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: getOptionsGridTemplate(opts.length),
                  gap: 1.25,
                  alignItems: 'stretch',
                }}
              >
                {opts.map((opt, optionIndex) => {
                  const optionId = opt.optionId ?? opt.id
                  const selected = answers[currentItem.itemId] === optionId
                  return (
                    <Paper
                      key={optionId}
                      elevation={0}
                      onClick={() => setAnswer(currentItem.itemId, optionId)}
                      sx={(theme) => {
                        const accent = useGradient ? getOptionGradientColor(theme, optionIndex, opts.length) : null
                        return {
                          p: 1.5,
                          pl: 2,
                          height: '100%',
                          minWidth: 0,
                          overflow: 'hidden',
                          borderRadius: 1.5,
                          border: 2,
                          borderColor: selected ? 'primary.main' : 'divider',
                          borderLeftColor: accent ? accent : undefined,
                          borderLeftWidth: accent ? 4 : undefined,
                          bgcolor: selected
                            ? (t) => alpha(t.palette.primary.main, 0.06)
                            : accent
                              ? alpha(accent, 0.12)
                              : 'action.hover',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
                          boxShadow: selected ? `0 6px 18px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
                          '&:hover': {
                            borderColor: selected ? 'primary.main' : 'action.selected',
                            borderLeftColor: accent || undefined,
                            bgcolor: selected
                              ? (t) => alpha(t.palette.primary.main, 0.08)
                              : accent
                                ? alpha(accent, 0.18)
                                : 'action.selected',
                          },
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                        <Radio
                          size="small"
                          value={optionId}
                          checked={selected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setAnswer(currentItem.itemId, e.target.value)}
                          sx={{
                            p: 0.5,
                            mt: 0.125,
                            alignSelf: 'flex-start',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={500} sx={OPTION_LABEL_SX}>
                          {opt.label ?? opt.title}
                        </Typography>
                      </Box>
                    </Paper>
                  )
                })}
              </RadioGroup>
            )
          })()}

          {currentAnswerType === ANSWER_TYPES.optionSelectMultiple && (() => {
            const opts = getOptionsForItem(currentItem)
            const useGradient = isLogicalGradient(opts)
            return (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: getOptionsGridTemplate(opts.length),
                  gap: 1.25,
                  alignItems: 'stretch',
                }}
              >
                {opts.map((opt, optionIndex) => {
                  const optionId = opt.optionId ?? opt.id
                  const checked = (answers[currentItem.itemId] ?? []).includes(optionId)
                  return (
                    <Paper
                      key={optionId}
                      elevation={0}
                      onClick={() => toggleMultiOption(currentItem.itemId, optionId)}
                      sx={(theme) => {
                        const accent = useGradient ? getOptionGradientColor(theme, optionIndex, opts.length) : null
                        return {
                          p: 1.5,
                          pl: 2,
                          height: '100%',
                          minWidth: 0,
                          overflow: 'hidden',
                          borderRadius: 1.5,
                          border: 2,
                          borderColor: checked ? 'primary.main' : 'divider',
                          borderLeftColor: accent ? accent : undefined,
                          borderLeftWidth: accent ? 4 : undefined,
                          bgcolor: checked
                            ? (t) => alpha(t.palette.primary.main, 0.06)
                            : accent
                              ? alpha(accent, 0.12)
                              : 'action.hover',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
                          boxShadow: checked ? `0 6px 18px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
                          '&:hover': {
                            borderColor: checked ? 'primary.main' : 'action.selected',
                            borderLeftColor: accent || undefined,
                            bgcolor: checked
                              ? (t) => alpha(t.palette.primary.main, 0.08)
                              : accent
                                ? alpha(accent, 0.18)
                                : 'action.selected',
                          },
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation()
                            toggleMultiOption(currentItem.itemId, optionId)
                          }}
                          sx={{
                            p: 0.5,
                            mt: 0.125,
                            alignSelf: 'flex-start',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={500} sx={OPTION_LABEL_SX}>
                          {opt.label ?? opt.title}
                        </Typography>
                      </Box>
                    </Paper>
                  )
                })}
              </Box>
            )
          })()}

          {currentAnswerType === ANSWER_TYPES.textBox && (
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="Enter your answer…"
              value={answers[currentItem.itemId] ?? ''}
              onChange={(e) => setAnswer(currentItem.itemId, e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                },
              }}
            />
          )}
        </Paper>

        {submitError && (
          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
              {submitError}
            </Typography>
          </Box>
        )}

        {/* Nav buttons: placed directly below options so they stay close to the question */}
        <Paper
          elevation={0}
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={canShowBackToList ? onBack : goPrev}
            disabled={submitting || (safeIndex === 0 && draftKey && !hasAttemptedDraftRestore)}
            variant={safeIndex === 0 ? 'text' : 'outlined'}
            size="medium"
          >
            {safeIndex === 0 && draftKey && !hasAttemptedDraftRestore
              ? 'Loading…'
              : safeIndex === 0
                ? backButtonLabel
                : prevButtonLabel}
          </Button>
          {isLast ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canProceed || submitting}
              endIcon={submitting ? null : <CheckCircleOutlineIcon />}
              size="medium"
            >
              {submitButtonLabel}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goNext}
              disabled={!canProceed}
              size="medium"
            >
              {nextButtonLabel}
            </Button>
          )}
        </Paper>
      </Box>
    </Box>
  )
}
